import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "types";
import {
  MOCK_ADD_ON_FORM_VALUES,
  MOCK_ADD_ON_TABLES,
  MOCK_ADDITIONAL_EQUIPMENT,
  MOCK_BOX_FORM_VALUES,
  MOCK_CENTRE_OF_GRAVITY_FORM,
  MOCK_CHASSIS_LOADS,
  MOCK_DIAGRAM_PAGES,
  MOCK_LOAD_DISTRIBUTION_FORM_VALUES,
  buildLoadDistributionPdfData,
  MOCK_QUOTATION_BOX_STRUCTURE,
  MOCK_QUOTATION_FORM_VALUES,
  MOCK_QUOTATION_LOCKS,
  MOCK_QUOTATION_SAFETY_VEHICLE,
  MOCK_QUOTATION_STANDARD_95,
  MOCK_QUOTATION_TYING_EQUIPMENT,
  MOCK_VEHICLE_FORM_VALUES,
  MOCK_VEHICLE_MEASURES,
  MOCK_WEIGHT_AND_WALL_FORM_VALUES,
} from "../components/WorkOrderDetails/data";
import { initialState } from "./initialState";

const selectDomain = (state?: RootState) =>
  state?.workOrderState || initialState;

export const selectWorkOrderLoading = createSelector(
  [selectDomain],
  (state) => state.loading
);

export const selectWorkOrderDetailLoading = createSelector(
  [selectDomain],
  (state) => state.detailLoading
);

export const selectWorkOrderError = createSelector(
  [selectDomain],
  (state) => state.error
);

export const selectStatusFilter = createSelector(
  [selectDomain],
  (state) => state.statusFilter
);

export const selectWorkOrders = createSelector(
  [selectDomain],
  (state) => state.workOrders
);

export const selectFilteredWorkOrders = createSelector(
  [selectDomain],
  (state) => {
    if (state.statusFilter === "ALL") {
      return state.workOrders;
    }

    return state.workOrders.filter(
      (workOrder) => workOrder.status === state.statusFilter
    );
  }
);

export const selectSelectedWorkOrderId = createSelector(
  [selectDomain],
  (state) => state.selectedWorkOrderId
);

export const selectWorkOrderVehicleDetails = createSelector(
  [selectDomain],
  (state) => state.vehicleDetails
);

export const selectVehicleDetailsForm = createSelector(
  [selectDomain],
  (state) => state.vehicleDetailsForm ?? MOCK_VEHICLE_FORM_VALUES
);

export const selectBoxDetailsForm = createSelector(
  [selectDomain],
  (state) => state.boxDetailsForm ?? MOCK_BOX_FORM_VALUES
);

export const selectWeightAndWallForm = createSelector(
  [selectDomain],
  (state) => state.weightAndWallForm ?? MOCK_WEIGHT_AND_WALL_FORM_VALUES
);

export const selectLoadDistributionForm = createSelector(
  [selectDomain],
  (state) => state.loadDistributionForm ?? MOCK_LOAD_DISTRIBUTION_FORM_VALUES
);

export const selectLoadDistributionPdfData = createSelector(
  [selectLoadDistributionForm],
  (form) => buildLoadDistributionPdfData(form)
);

export const selectQuotationForm = createSelector(
  [selectDomain],
  (state) => state.quotationForm ?? MOCK_QUOTATION_FORM_VALUES
);

export const selectQuotationBoxStructure = createSelector(
  [selectDomain],
  (state) => state.quotationBoxStructure ?? MOCK_QUOTATION_BOX_STRUCTURE
);

export const selectQuotationTyingEquipment = createSelector(
  [selectDomain],
  (state) => state.quotationTyingEquipment ?? MOCK_QUOTATION_TYING_EQUIPMENT
);

export const selectQuotationSafetyVehicle = createSelector(
  [selectDomain],
  (state) => state.quotationSafetyVehicle ?? MOCK_QUOTATION_SAFETY_VEHICLE
);

export const selectQuotationLocks = createSelector(
  [selectDomain],
  (state) => state.quotationLocks ?? MOCK_QUOTATION_LOCKS
);

export const selectQuotationStandard95 = createSelector(
  [selectDomain],
  (state) => state.quotationStandard95 ?? MOCK_QUOTATION_STANDARD_95
);

export const selectCentreOfGravityForm = createSelector(
  [selectDomain],
  (state) => state.centreOfGravityForm ?? MOCK_CENTRE_OF_GRAVITY_FORM
);

export const selectChassisLoads = createSelector(
  [selectDomain],
  (state) => state.chassisLoads ?? MOCK_CHASSIS_LOADS
);

export const selectVehicleMeasures = createSelector(
  [selectDomain],
  (state) => state.vehicleMeasures ?? MOCK_VEHICLE_MEASURES
);

export const selectAdditionalEquipment = createSelector(
  [selectDomain],
  (state) => state.additionalEquipment ?? MOCK_ADDITIONAL_EQUIPMENT
);

const defaultAddOnTyingStripModal = {
  open: false,
  height: "",
  note: "",
};

export const selectAddOnDetails = createSelector([selectDomain], (state) =>
  state.addOnDetails ?? {
    form: MOCK_ADD_ON_FORM_VALUES,
    tables: MOCK_ADD_ON_TABLES,
    tyingStripModal: defaultAddOnTyingStripModal,
  }
);

export const selectAddOnForm = createSelector(
  [selectAddOnDetails],
  (addOnDetails) => addOnDetails.form
);

export const selectAddOnTables = createSelector(
  [selectAddOnDetails],
  (addOnDetails) => addOnDetails.tables
);

export const selectAddOnTyingStripModal = createSelector(
  [selectAddOnDetails],
  (addOnDetails) => addOnDetails.tyingStripModal
);

export const selectAddOnTyingStripModalOpen = createSelector(
  [selectAddOnTyingStripModal],
  (tyingStripModal) => tyingStripModal?.open ?? false
);

export const selectDiagramView = createSelector(
  [selectDomain],
  (state) => state.diagramView
);

export const selectDiagramActiveDepartment = createSelector(
  [selectDiagramView],
  (diagramView) => diagramView.activeDepartment
);

export const selectDiagramActivePage = createSelector(
  [selectDiagramView],
  (diagramView) => diagramView.activePage
);

export const selectDiagramPages = createSelector(
  [selectDiagramView],
  (diagramView) => (diagramView.pages.length > 0 ? diagramView.pages : MOCK_DIAGRAM_PAGES)
);

export const selectActiveDiagramPageData = createSelector(
  [selectDiagramPages, selectDiagramActivePage],
  (pages, activePage) => pages[activePage] ?? pages[0] ?? null
);

export const selectActiveDetailTab = createSelector(
  [selectDomain],
  (state) => state.activeDetailTab
);

export const selectWorkOrderById = (state: RootState, workOrderId: number) => {
  const { workOrders, selectedWorkOrderId, vehicleDetails } =
    selectDomain(state);

  if (selectedWorkOrderId === workOrderId && vehicleDetails) {
    return vehicleDetails;
  }

  return null;
};
